import React, { useState } from "react";
import { toast } from "sonner";
import { Lock, Send } from "lucide-react";
import { StarSelector } from "./StarRating";
import { useAuth } from "../context/AuthContext";
import { createReview } from "../lib/api";

// REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
const startSignIn = () => {
  const redirectUrl = window.location.origin + window.location.pathname + window.location.search;
  window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
};

export const ReviewForm = ({ productSlug = null, onSubmitted }) => {
  const { user } = useAuth();
  const [rating, setRating] = useState(5);
  const [title, setTitle] = useState("");
  const [body, setBody] = useState("");
  const [submitting, setSubmitting] = useState(false);

  if (!user) {
    return (
      <div
        data-testid="review-form-signin-prompt"
        className="p-8 border border-[#2A2A2A] rounded-3xl bg-[#0e0e0e] flex flex-col md:flex-row items-start md:items-center justify-between gap-4"
      >
        <div>
          <div className="label-mono flex items-center gap-2"><Lock size={11} /> Members only</div>
          <h3 className="mt-3 text-white text-xl tracking-tight font-medium">Share your lab's experience.</h3>
          <p className="mt-2 text-[#B7BCC7] text-sm max-w-lg leading-relaxed">
            Verified researchers can post a review after signing in. Please keep feedback focused on
            product quality — purity, packaging, documentation and shipping — never on health outcomes.
          </p>
        </div>
        <button
          onClick={startSignIn}
          data-testid="review-form-signin-btn"
          className="btn-primary inline-flex items-center gap-2 shrink-0"
        >
          Sign in to Review
        </button>
      </div>
    );
  }

  const disabled = title.trim().length < 3 || body.trim().length < 10 || submitting;

  const submit = async (e) => {
    e.preventDefault();
    if (disabled) return;
    setSubmitting(true);
    try {
      const created = await createReview({
        product_slug: productSlug || null,
        rating,
        title: title.trim(),
        body: body.trim(),
      });
      toast.success("Thanks — your review is live.");
      setTitle("");
      setBody("");
      setRating(5);
      if (onSubmitted) onSubmitted(created);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not submit review.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <form
      onSubmit={submit}
      data-testid="review-form"
      className="p-8 border border-[#2A2A2A] rounded-3xl bg-[#0e0e0e] flex flex-col gap-6"
    >
      <div>
        <div className="label-mono">Post a review</div>
        <div className="mt-2 text-white text-xl tracking-tight font-medium">
          Signed in as {user.name || user.email}
        </div>
      </div>

      <div>
        <label className="label-mono block">Rating</label>
        <div className="mt-3">
          <StarSelector value={rating} onChange={setRating} />
        </div>
      </div>

      <div>
        <label htmlFor="rv-title" className="label-mono block">Title</label>
        <input
          id="rv-title"
          value={title}
          onChange={(e) => setTitle(e.target.value)}
          maxLength={140}
          data-testid="review-form-title"
          placeholder="Short summary of your experience"
          className="mt-3 w-full h-11 px-4 rounded-xl bg-[#0B0B0B] border border-[#2A2A2A] text-white placeholder:text-[#6E7784] focus:border-[#1565FF] focus:outline-none transition-colors"
        />
      </div>

      <div>
        <label htmlFor="rv-body" className="label-mono block">Review</label>
        <textarea
          id="rv-body"
          value={body}
          onChange={(e) => setBody(e.target.value)}
          maxLength={2000}
          rows={5}
          data-testid="review-form-body"
          placeholder="Focus on quality, packaging, documentation and shipping. Please avoid health-outcome claims."
          className="mt-3 w-full px-4 py-3 rounded-xl bg-[#0B0B0B] border border-[#2A2A2A] text-white placeholder:text-[#6E7784] focus:border-[#1565FF] focus:outline-none transition-colors leading-relaxed"
        />
        <div className="mt-2 text-[11px] font-mono text-[#6E7784]">
          {body.length}/2000 · min 10 characters
        </div>
      </div>

      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="text-[11px] font-mono text-[#6E7784] tracking-widest uppercase">
          Research-use context · No efficacy claims
        </div>
        <button
          type="submit"
          disabled={disabled}
          data-testid="review-form-submit"
          className="btn-primary inline-flex items-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
        >
          <Send size={14} /> {submitting ? "Publishing…" : "Publish Review"}
        </button>
      </div>
    </form>
  );
};
